import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Sparkles, 
  Settings, 
  FileText, 
  CheckCircle2, 
  Loader2,
  TrendingUp,
  Search,
  Code,
  Eye,
  X
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdvancedContentSettings, AdvancedContentGenerationSettings } from "./AdvancedContentSettings";

interface AdvancedGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  articleId: string;
  articleTitle: string;
  articleData?: any;
  analysisData?: any;
  userId: string;
  websiteId: string;
  userSettings?: any; // User's saved settings
  onSuccess: (generatedArticleId: string) => void;
  autoStart?: boolean; // If true, skip settings and start generation immediately
}

interface GenerationStep {
  id: string;
  title: string;
  description: string;
  icon: any;
  active: boolean;
  completed: boolean;
}

export const AdvancedGenerationModal: React.FC<AdvancedGenerationModalProps> = ({
  isOpen,
  onClose,
  articleId,
  articleTitle,
  articleData,
  analysisData,
  userId,
  websiteId,
  userSettings,
  onSuccess,
  autoStart = false
}) => {
  const [showSettings, setShowSettings] = useState(!autoStart);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedArticleId, setGeneratedArticleId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [progress, setProgress] = useState(0); // backend/true progress
  const [displayProgress, setDisplayProgress] = useState(0); // smoothed UI progress
  const [targetProgress, setTargetProgress] = useState(0); // target for smoothing
  const [isCompleted, setIsCompleted] = useState(false);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(180); // 3 minutes default

  const [steps, setSteps] = useState<GenerationStep[]>([
    {
      id: 'research',
      title: 'Research & Planning',
      description: 'Analyzing competitors and planning content strategy',
      icon: Search,
      active: false,
      completed: false
    },
    {
      id: 'content',
      title: 'Content Generation',
      description: 'Creating high-quality, optimized content',
      icon: FileText,
      active: false,
      completed: false
    },
    {
      id: 'seo',
      title: 'SEO Optimization',
      description: 'Applying schema markup and technical SEO',
      icon: TrendingUp,
      active: false,
      completed: false
    },
    {
      id: 'finalize',
      title: 'Finalization',
      description: 'Final checks and optimization',
      icon: CheckCircle2,
      active: false,
      completed: false
    }
  ]);

  // Cleanup polling interval on unmount or when modal closes
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      toastShownRef.current = false;
      generationStartedRef.current = false; // Reset generation flag on unmount
    };
  }, []);

  // Reset flags when modal opens/closes or article changes
  useEffect(() => {
    if (!isOpen) {
      toastShownRef.current = false;
      generationStartedRef.current = false; // Reset generation flag when modal closes
      setIsGenerating(false); // Reset generating state when modal closes
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    } else {
      // When modal opens, reset the ref and state to allow new generation
      generationStartedRef.current = false;
      setIsGenerating(false);
    }
  }, [isOpen, articleId]); // Reset when articleId changes too

  // Auto-start generation if autoStart is true
  useEffect(() => {
    // Prevent duplicate calls - check if generation already started
    if (autoStart && isOpen && !isGenerating && !generationStartedRef.current) {
      setShowSettings(false);
      // Don't set the ref here - let startGeneration() set it after successful API call
      startGeneration();
    }
    
    // Reset ref when modal closes
    if (!isOpen) {
      generationStartedRef.current = false;
    }
  }, [autoStart, isOpen, isGenerating]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isGenerating && estimatedTimeRemaining > 0) {
      interval = setInterval(() => {
        setEstimatedTimeRemaining(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isGenerating, estimatedTimeRemaining]);

  // Track last progress update time to detect stuck progress
  const [lastProgressUpdate, setLastProgressUpdate] = useState(Date.now());
  const [stuckProgressStart, setStuckProgressStart] = useState<number | null>(null);
  
  // Refs to prevent duplicate toasts and manage polling
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const toastShownRef = useRef<boolean>(false);
  const generationStartedRef = useRef<boolean>(false);

  // Update last progress update time when targetProgress changes
  useEffect(() => {
    if (targetProgress > displayProgress) {
      setLastProgressUpdate(Date.now());
      setStuckProgressStart(null);
    }
  }, [targetProgress]);

  // Smooth progress animation toward targetProgress - simple and reliable
  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayProgress(prev => {
        const target = isCompleted ? 100 : targetProgress;
        
        // If already at or past target, stay there
        if (prev >= target) return prev;
        
        // Smooth animation: calculate step size based on distance
        const distance = target - prev;
        
        // If very close (within 0.5%), move directly to target
        if (distance < 0.5) {
          return target;
        }
        
        // Progressive step size: faster when far, slower when close
        // This creates smooth acceleration/deceleration effect
        const step = distance > 20 
          ? Math.min(4, distance / 6)      // Fast when far (max 4% per update)
          : distance > 10
          ? Math.min(2, distance / 5)      // Medium speed (max 2% per update)
          : distance > 3
          ? Math.min(1, distance / 3)     // Slow when close (max 1% per update)
          : Math.min(0.5, distance / 2);  // Very slow when very close (max 0.5% per update)
        
        const newProgress = Math.min(prev + step, target);
        return newProgress;
      });
    }, 80); // Update every 80ms for smooth, responsive animation
    return () => clearInterval(interval);
  }, [targetProgress, isCompleted]);

  const handleClose = () => {
    if (!isGenerating || generatedArticleId) {
      onClose();
    }
  };

  const handleSaveSettings = (settings: AdvancedContentGenerationSettings) => {
    // Prevent duplicate calls if generation already started
    if (isGenerating || generationStartedRef.current) {
      console.warn('⚠️ Generation already started, ignoring settings save');
      return;
    }
    setShowSettings(false);
    startGeneration();
  };

  const startGeneration = async () => {
    // CRITICAL: Set ref IMMEDIATELY to prevent race conditions from multiple clicks
    // This must happen BEFORE any async operations or state updates
    if (generationStartedRef.current) {
      console.warn('⚠️ Generation already started, ignoring duplicate call');
      return;
    }
    generationStartedRef.current = true;
    
    // Prevent duplicate calls with multiple guards
    // Check if we're already polling for a job - if so, allow it to continue
    const isAlreadyPolling = pollingIntervalRef.current !== null;
    
    console.log('🔍 startGeneration called:', {
      isGenerating,
      generationStartedRef: generationStartedRef.current,
      isAlreadyPolling,
      articleId
    });
    
    // Additional check for state (though ref should catch it first)
    if (isGenerating && !isAlreadyPolling) {
      console.warn('⚠️ Generation already in progress, ignoring duplicate call');
      generationStartedRef.current = false; // Reset since we're returning early
      return;
    }
    
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    toastShownRef.current = false;
    
    setIsGenerating(true);
    setProgress(0);
    setTargetProgress(5);
    setDisplayProgress(0);
    setIsCompleted(false);
    setEstimatedTimeRemaining(180);
    setLastProgressUpdate(Date.now());
    setStuckProgressStart(null);

    try {
      // Step 1: Research & Planning
      setSteps(prev => prev.map(step => ({
        ...step,
        active: step.id === 'research',
        completed: false
      })));
      setCurrentStep('Analyzing competitors and planning content strategy...');
      setProgress(10);
      setTargetProgress(15);
      
      // Step 2: Content Generation
      setSteps(prev => prev.map(step => ({
        ...step,
        active: step.id === 'content',
        completed: step.id === 'research'
      })));
      setCurrentStep('Creating high-quality, optimized content...');
      setProgress(25);
      setTargetProgress(30);

      // Call the real enhanced article generation function
      console.log('🚀 Starting real article generation for:', articleTitle);
      
      // Merge UI settings with per-article target word count if available
      const mergedSettings = {
        ...userSettings,
        word_count_target: (userSettings?.word_count_target || articleData?.estimated_word_count) ? (userSettings?.word_count_target || articleData?.estimated_word_count) : 2500,
        language: userSettings?.language,
        writing_style: userSettings?.writing_style,
        image_style: userSettings?.image_style,
      };

      const { data, error } = await supabase.functions.invoke('enhanced-article-generation', {
        body: {
          article_id: articleId,
          user_id: userId,
          website_id: websiteId,
          settings: mergedSettings
        }
      });

      if (error) {
        generationStartedRef.current = false; // Reset on error
        throw error;
      }
      
      // Ref is already set at the start of the function, no need to set again

      // Path A: legacy immediate result with article_id
      if (data?.success && data?.article_id) {
        // Step 3: SEO Optimization
        setTargetProgress(70);
        setSteps(prev => prev.map(step => ({
          ...step,
          active: step.id === 'seo',
          completed: step.id === 'research' || step.id === 'content'
        })));
        setCurrentStep('Applying schema markup and technical SEO...');
        setProgress(75);
        setTargetProgress(85);

        // Step 4: Finalization
        setSteps(prev => prev.map(step => ({
          ...step,
          active: step.id === 'finalize',
          completed: step.id !== 'finalize'
        })));
        setCurrentStep('Final checks and optimization...');
        setProgress(90);
        setTargetProgress(95);

        // Complete all steps
        setSteps(prev => prev.map(step => ({
          ...step,
          active: false,
          completed: true
        })));
        setCurrentStep('Article generation completed!');
        setProgress(100);
        setIsCompleted(true);
        setTargetProgress(100);

        setGeneratedArticleId(data.article_id);
        setIsGenerating(false);
        generationStartedRef.current = false; // Reset for next time
        
        // Only show toast once
        if (!toastShownRef.current) {
          toastShownRef.current = true;
          toast.success('Article generated successfully!');
        }
        
        // Automatically open article preview after 2 seconds
        setTimeout(() => {
          onSuccess(data.article_id);
          handleClose();
        }, 2000);
      } else if (data?.success && data?.job_id) {
        // Path B: async job flow – poll Supabase until completed then open preview
        const jobId = data.job_id as string;
        
        // If this is an existing job (not a new one), log it
        if (data?.message === 'Job already in progress') {
          console.log('📋 Resuming polling for existing job:', jobId);
        } else {
          console.log('✅ New job created:', jobId);
        }
        
        setCurrentStep('Starting article generation...');
        setProgress(10);
        setTargetProgress(10);

        const pollIntervalMs = 2000;
        // Clear any existing interval before creating a new one
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        pollingIntervalRef.current = setInterval(async () => {
          try {
            const { data: job, error: jobError } = await supabase
              .from('article_generation_jobs')
              .select('status, progress, current_step, error_message')
              .eq('id', jobId)
              .single();

            if (jobError) {
              console.error('Job polling error:', jobError);
              return;
            }

            if (job?.current_step) {
              setCurrentStep(job.current_step);
            }
            if (typeof job?.progress === 'number' && job.progress >= 0) {
              // Update progress - always move forward, never backward
              setProgress(prev => {
                return Math.max(prev, job.progress);
              });
              setTargetProgress(prev => Math.max(prev, job.progress));
              // Reset stuck detection when we get a real update
              setLastProgressUpdate(Date.now());
              setStuckProgressStart(null);
            }

            if (job?.status === 'completed') {
              // Clear the interval and reset ref
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }

              // Only process completion once
              if (toastShownRef.current) {
                return;
              }

              // Fetch the most recent generated article for this articleId
              const { data: gen, error: genErr } = await supabase
                .from('generated_articles')
                .select('id')
                .eq('article_id', articleId)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

              if (genErr || !gen?.id) {
                throw new Error('Article generated but not found');
              }

              toastShownRef.current = true;
              setGeneratedArticleId(gen.id);
              setIsGenerating(false);
              generationStartedRef.current = false; // Reset for next time
              setIsCompleted(true);
              // Smoothly animate to 100%
              setTargetProgress(100);
              setProgress(100);
              setLastProgressUpdate(Date.now());
              // Wait a moment for the progress bar to animate to 100% before closing
              setTimeout(() => {
                toast.success('Article generated successfully!');
                onSuccess(gen.id);
                handleClose();
              }, 1200); // Slightly longer to see completion
            }

            if (job?.status === 'failed') {
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              setIsGenerating(false);
              generationStartedRef.current = false; // Reset for next time
              throw new Error(job?.error_message || 'Generation failed');
            }
          } catch (e: any) {
            console.error('Polling error:', e);
            // Clear interval on error to prevent further polling
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        }, pollIntervalMs);
      } else {
        generationStartedRef.current = false; // Reset if no valid response
        throw new Error(data?.error || 'Failed to start article generation');
      }
      
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(`Failed to generate article: ${error.message}`);
      setIsGenerating(false);
      generationStartedRef.current = false; // Reset for next time
      // Clear polling interval on error
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl w-full p-0 overflow-hidden border-0 bg-transparent shadow-none" hideCloseButton>
        <DialogTitle className="sr-only">Advanced Article Generation</DialogTitle>
        <DialogDescription className="sr-only">Generate high-quality articles with AI</DialogDescription>
        
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden relative border border-gray-200">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          <div className="flex min-h-[500px]">
            {showSettings ? (
              // Settings view - full width
              <div className="w-full p-6">
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    Advanced Article Generation
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Configure advanced settings for: <span className="font-semibold">{articleTitle}</span>
                  </p>
                </div>
                <AdvancedContentSettings
                  onSave={handleSaveSettings}
                  onCancel={handleClose}
                  analysisData={analysisData}
                  articleData={articleData}
                />
              </div>
            ) : (
              <>
                {/* Left side - Animation/Logo */}
                <div className="w-2/5 bg-gradient-to-br from-primary/8 via-primary/5 to-primary/12 relative overflow-hidden border-r border-primary/20 flex items-center justify-center">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-20 h-20 border-2 border-primary rounded-full"></div>
                    <div className="absolute top-32 right-16 w-16 h-16 border-2 border-primary rounded-lg rotate-45"></div>
                    <div className="absolute bottom-20 left-20 w-12 h-12 border-2 border-primary rounded-full"></div>
                    <div className="absolute bottom-32 right-10 w-24 h-24 border-2 border-primary rounded-lg rotate-12"></div>
                  </div>
                  
                  {/* Spinning Logo */}
                  <div className="relative">
                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-white/20 rounded-full blur-2xl scale-150 animate-pulse-glow"></div>
                      
                      {/* Spinning logo container */}
                      <div className="relative w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30 shadow-2xl animate-float">
                        <div className="w-24 h-24">
                          <img
                            src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Untitled-222.png"
                            alt="Seoro Logo"
                            className={`w-full h-full object-contain ${!generatedArticleId ? 'animate-[spin_3s_linear_infinite]' : ''}`}
                          />
                        </div>
                      </div>
                      
                  </div>
                  
                  {/* Bottom text */}
                  <div className="absolute bottom-6 left-0 right-0 text-center">
                    <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 inline-block border border-white/20">
                      <span className="text-white font-semibold text-sm">Blog in progress</span>
                    </div>
                  </div>
                </div>

                {/* Right side - Progress */}
                <div className="flex-1 p-6 flex flex-col justify-center">
                  <div className="w-full max-w-sm mx-auto">
                    {generatedArticleId ? (
                      <div className="text-center">
                        <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 className="h-10 w-10 text-primary-foreground" />
                        </div>
                        
                        <h3 className="text-xl font-bold text-foreground mb-3">
                          Article Generated!
                        </h3>
                        
                        <p className="text-muted-foreground text-sm mb-6">
                          Your article has been successfully generated and is ready for review.
                        </p>

                        <div>
                          <Button
                            onClick={() => {
                              onSuccess(generatedArticleId)
                              handleClose()
                            }}
                            className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-lg font-semibold hover:bg-primary/90 transition-all duration-200"
                          >
                            View Article
                          </Button>
                        </div>
                      </div>

                    ) : (
                      <>
                        <div className="text-center mb-6">
                          <h3 className="text-2xl font-bold text-foreground mb-2">
                            Creating your article
                          </h3>
                          <p className="text-muted-foreground text-sm">
                            Estimated time remaining: {estimatedTimeRemaining > 0 ? `~${Math.ceil(estimatedTimeRemaining / 60)} min` : 'Calculating...'}
                          </p>
                        </div>

                        {/* Progress Steps */}
                        <div className="space-y-2 mb-6">
                          {steps.map((step, index) => {
                            const Icon = step.icon;
                            return (
                              <div
                                key={step.id}
                                className={`flex items-center p-2 rounded-lg transition-all duration-300 ${
                                  step.active 
                                    ? 'bg-primary/8 border border-primary/20' 
                                    : step.completed 
                                      ? 'bg-primary/5 border border-primary/10' 
                                      : 'bg-muted border border-border'
                                }`}
                              >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                                  step.active 
                                    ? 'bg-primary text-primary-foreground' 
                                    : step.completed 
                                      ? 'bg-primary/80 text-primary-foreground' 
                                      : 'bg-muted-foreground/20 text-muted-foreground'
                                }`}>
                                  {step.completed ? (
                                    <CheckCircle2 className="w-3 h-3" />
                                  ) : step.active ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Icon className="w-3 h-3" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className={`font-medium text-sm ${
                                    step.active ? 'text-primary' : step.completed ? 'text-primary' : 'text-foreground'
                                  }`}>
                                    {step.title}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Overall Progress */}
                        <div className="bg-muted/50 rounded-lg p-3 mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-foreground">Progress</span>
                            <span className="text-sm text-muted-foreground">{Math.round(displayProgress)}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-primary to-primary/80 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${displayProgress}%` }}
                            />
                          </div>
                        </div>


                        {/* Article Title */}
                        <div className="bg-gradient-to-r from-primary/8 to-primary/5 rounded-lg p-3 border border-primary/20">
                          <div className="font-bold text-foreground text-sm leading-tight line-clamp-2">{articleTitle}</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
